import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTwitterLineItems } from '../api';
import type { TwitterCampaignWithLineItems } from '../api';
import type { TwitterGridRow, TwitterLineItemRow } from '../types';
import { isTwitterLineItemRow } from '../types';
import { getApiErrorMessage } from 'lib/api';

const EMPTY: string[] = [];

const useTwitterLineItemsData = () => {
  const { setValue } = useFormContext();
  const accountId = (useWatch({ name: 'accountId' }) as string | undefined) ?? '';
  const selectedCampaigns = (useWatch({ name: 'selectedCampaigns' }) as string[] | undefined) ?? EMPTY;
  const selectedLineItems = (useWatch({ name: 'selectedLineItems' }) as string[] | undefined) ?? EMPTY;
  const filterByName = (useWatch({ name: 'filterLineItemsByName' }) as string[] | undefined) ?? EMPTY;

  const { data, isLoading, isError, error } = useTwitterLineItems({
    params: { accountId, campaignIds: selectedCampaigns },
  });

  const lineItemsData = useMemo<TwitterCampaignWithLineItems[]>(() => data ?? [], [data]);

  useEffect(() => {
    if (data) {
      setValue('$lineItemsData', data, { shouldValidate: true });
    }
  }, [data, setValue]);

  const allRows = useMemo<TwitterGridRow[]>(
    () =>
      lineItemsData.flatMap((campaign) => [
        campaign,
        ...campaign.line_items.map<TwitterLineItemRow>((lineItem) => ({
          ...lineItem,
          campaign_id: campaign.campaign_id,
          campaign_name: campaign.campaign_name,
          account_id: campaign.account_id,
          isLineItem: true,
        })),
      ]),
    [lineItemsData],
  );

  const gridRows = useMemo<TwitterGridRow[]>(() => {
    if (!filterByName.length) return allRows;

    const selected = new Set(selectedLineItems);
    return allRows.filter((row) => {
      if (!isTwitterLineItemRow(row)) return true;
      if (selected.has(row.line_item_id)) return true;

      const name = row.line_item_name.toLowerCase();
      return filterByName.every((chip) => name.includes(chip.toLowerCase()));
    });
  }, [allRows, filterByName, selectedLineItems]);

  const selectedLineItemRows = useMemo(() => {
    const selected = new Set(selectedLineItems);
    return allRows.filter(
      (row): row is TwitterLineItemRow => isTwitterLineItemRow(row) && selected.has(row.line_item_id),
    );
  }, [allRows, selectedLineItems]);

  const selectedFlows = useMemo(
    () =>
      [...new Set(selectedLineItemRows.map((row) => row.flow).filter((flow): flow is string => !!flow))].sort(),
    [selectedLineItemRows],
  );

  return {
    lineItemsData,
    gridRows,
    selectedLineItems,
    selectedLineItemRows,
    selectedFlows,
    isLoading,
    isError,
    errorMessage: isError ? getApiErrorMessage(error, 'Failed to load ad groups') : undefined,
  };
};

export default useTwitterLineItemsData;
